import { Download } from "@tamagui/lucide-icons";
import { BlurView } from "expo-blur";
import { useEffect, useRef } from "react";
import { ScrollView, XStack, YStack } from "tamagui";

import { useCreateHomeCarePlanPdfLink } from "@/api/generated";
import { HomeCarePlanStep } from "@/api/generated/models/HomeCarePlanStep";
import {
  Button,
  CloseButton,
  Heading,
  Link,
  Paragraph,
  Section,
  Separator,
  Text,
} from "@/components";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import useRedirectUrl from "@/hooks/useRedirectUrl";
import { downloadMessageFile } from "@/utils/downloadMessageFile";

type NormalizedPlanItem = {
  title: string;
  paragraphs: string[];
};

const parseTimestamp = (timestamp?: string | null) => {
  if (!timestamp) return null;
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimestampLabels = (timestamp?: string | null) => {
  const parsed = parseTimestamp(timestamp);
  if (!parsed) {
    return { dateLabel: "", timeLabel: "" };
  }

  const dateLabel = parsed.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeLabel = parsed.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return { dateLabel, timeLabel };
};

const normalizePlanItems = (plan: HomeCarePlanStep[]): NormalizedPlanItem[] =>
  (Array.isArray(plan) ? plan : [])
    .map((step) => {
      const title = step.title?.trim() ?? "";
      const detail = step.detail?.trim() ?? "";

      if (!detail) {
        return title ? { title, paragraphs: [] } : null;
      }

      const paragraphs = detail.includes("\n")
        ? detail
            .split(/\n+/)
            .map((line) => line.trim())
            .filter(Boolean)
        : detail
            .split(/(?<=[.?!])\s+(?=[A-Z0-9])/)
            .map((segment) => segment.trim())
            .filter(Boolean);

      if (!title && paragraphs.length === 0) {
        return null;
      }

      return { title, paragraphs };
    })
    .filter((item): item is NormalizedPlanItem => Boolean(item));

export interface HomeCarePlanDisplayProps {
  assessment: string;
  plan: HomeCarePlanStep[];
  onClose: () => void;
  generatedAt?: string;
  messageId: string;
}

export default function HomeCarePlanDisplay({
  assessment,
  plan,
  onClose,
  generatedAt,
  messageId,
}: HomeCarePlanDisplayProps) {
  const conversationId = useCurrentConversationId();
  const convoIdRef = useRef(conversationId);
  const isSignedInStable = useIsSignedInStable();
  const redirectUrl = useRedirectUrl();

  const signInHref = {
    pathname: "/sign-in",
    params: { redirectUrl, currentConversationId: conversationId },
  };

  const { refetch, isFetching } = useCreateHomeCarePlanPdfLink(messageId!, {
    query: {
      enabled: false,
      gcTime: 0,
    },
  });

  useEffect(() => {
    if (conversationId !== convoIdRef.current) {
      onClose();
    }
    convoIdRef.current = conversationId;
  }, [conversationId, onClose]);

  const normalizedPlan = normalizePlanItems(plan);
  const { dateLabel, timeLabel } = formatTimestampLabels(generatedAt);
  const timestampLabel = [dateLabel, timeLabel].filter(Boolean).join(" • ");

  let downloadOrSignup;
  if (isSignedInStable) {
    downloadOrSignup = (
      <Button
        mode="tertiaryWithIcon"
        icon={Download}
        onPress={() =>
          downloadMessageFile({
            messageId: messageId!,
            path: "home-care-plan-pdf",
            fetchKey: refetch,
          })
        }
        disabled={isFetching}
      >
        Download
      </Button>
    );
  } else {
    downloadOrSignup = (
      <Text size="$sm" marginVertical="$150">
        <Link
          size="$sm"
          href={signInHref}
          dataTrackingId="home-care-plan-create-account"
        >
          Create an account
        </Link>
        &nbsp;or&nbsp;
        <Link
          size="$sm"
          href={signInHref}
          dataTrackingId="home-care-plan-log-in"
        >
          log in
        </Link>
        &nbsp;to download
      </Text>
    );
  }

  return (
    <YStack height="100%" position="relative">
      <YStack paddingTop="$200" paddingHorizontal="$200" gap="$300">
        <CloseButton onClose={onClose} dataTrackingId="home-care-plan-close" />
        <Section gap="$200">
          <Heading size="$lg">Your home care plan</Heading>
          <YStack gap="$100">
            <Text
              fontFamily="$serif"
              color="$textSecondary"
              size="$sm"
              fontStyle="italic"
            >
              Nori is not a replacement for a clinician.
            </Text>
            {timestampLabel && (
              <Text size="$sm" color="$textSecondary">
                {timestampLabel}
              </Text>
            )}
          </YStack>
        </Section>
        <Separator borderColor="$fillNeutral" />
      </YStack>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingBottom: "$800",
          paddingHorizontal: "$200",
          gap: "$300",
        }}
      >
        <Section>
          <YStack gap="$200">
            <Heading size="$md">Why you feel this way</Heading>
            <Paragraph color="$textPrimary" fontFamily="$serif">
              {assessment.trim()}
            </Paragraph>
            {normalizedPlan.map((step, index) => (
              <Section key={step.title} gap="$200">
                {!!step.title && <Heading size="$md">{step.title}</Heading>}
                <YStack gap="$75">
                  {step.paragraphs.map((paragraph, paragraphIndex) => (
                    <Paragraph
                      key={`${index}-${paragraphIndex}`}
                      color="$textPrimary"
                      fontFamily="$serif"
                    >
                      {paragraph}
                    </Paragraph>
                  ))}
                </YStack>
              </Section>
            ))}
          </YStack>
        </Section>
      </ScrollView>
      <BlurView
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          borderBottomRightRadius: 12,
          borderBottomLeftRadius: 12,
        }}
      >
        <XStack paddingVertical="$100" marginHorizontal="auto">
          {downloadOrSignup}
        </XStack>
      </BlurView>
    </YStack>
  );
}
